import React from "react";
import { lookupName } from "../data/designOptions";

// ---------- Shared primitives ----------
const Swatch = ({ option, active, onClick, testid }) => {
  const isColor = option.hex && option.hex !== "transparent";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      title={option.name}
      className={`group relative flex h-9 w-9 items-center justify-center transition-all duration-300 ease-out ${
        active
          ? "ring-2 ring-offset-2 ring-offset-[#0A0A0A] ring-[#B8996F]"
          : "ring-1 ring-white/15 hover:ring-white/40"
      }`}
      style={{ borderRadius: 0 }}
    >
      {isColor ? (
        <span
          aria-hidden
          className="block h-full w-full"
          style={{ backgroundColor: option.hex }}
        />
      ) : (
        <span
          aria-hidden
          className="block h-full w-full bg-[repeating-linear-gradient(45deg,#3a3a3a_0_4px,#222_4px_8px)]"
        />
      )}
    </button>
  );
};

export const OptionRow = ({ label, options, value, onChange, idPrefix }) => (
  <div className="border-b border-white/10 py-4">
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <span className="text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</span>
      <span
        className="font-[Outfit] text-[11px] text-white/80"
        data-testid={`${idPrefix}-current-name`}
      >
        {lookupName(options, value)}
      </span>
    </div>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <Swatch
          key={opt.id}
          option={opt}
          active={value === opt.id}
          onClick={() => onChange(opt.id)}
          testid={`${idPrefix}-swatch-${opt.id}`}
        />
      ))}
    </div>
  </div>
);

export const ChoicePills = ({ label, options, value, onChange, idPrefix }) => (
  <div className="border-b border-white/10 py-4">
    <span className="mb-3 block text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</span>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          data-testid={`${idPrefix}-pill-${opt.id}`}
          className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
            value === opt.id
              ? "bg-[#B8996F] text-[#0A0A0A]"
              : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
          }`}
        >
          {opt.name}
        </button>
      ))}
    </div>
  </div>
);

export const PresetGrid = ({ presets, activeId, onPick, idPrefix }) => (
  <div className="grid grid-cols-2 gap-2">
    {presets.map((p) => (
      <button
        key={p.id}
        type="button"
        onClick={() => onPick(p)}
        data-testid={`${idPrefix}-preset-${p.id}`}
        className={`group relative overflow-hidden border px-3 py-3 text-left transition-all duration-300 ${
          activeId === p.id
            ? "border-[#B8996F] bg-[#B8996F]/10"
            : "border-white/10 bg-white/[0.02] hover:border-white/30 hover:bg-white/[0.04]"
        }`}
      >
        <span className="block text-[10px] uppercase tracking-[0.22em] text-white/45">Preset</span>
        <span className="mt-1 block font-[Cormorant_Garamond] text-lg leading-tight text-white">
          {p.name}
        </span>
      </button>
    ))}
  </div>
);

// ---------- Panels ----------
const KITCHEN_OPTIONS = [
  { key: "cabinet", label: "Cabinet Color", source: "cabinet" },
  { key: "countertop", label: "Countertop Finish", source: "countertop" },
  { key: "backsplash", label: "Backsplash", source: "backsplash" },
  { key: "wall", label: "Wall Paint", source: "wall" },
  { key: "handle", label: "Handle Finish", source: "handle" },
  { key: "wood", label: "Wood Texture", source: "wood" },
];

export const KitchenControls = ({ config, swatches, onChange }) => (
  <>
    {KITCHEN_OPTIONS.map(({ key, label, source }) => (
      <OptionRow
        key={key}
        label={label}
        options={swatches[source]}
        value={config[key]}
        onChange={(v) => onChange(key, v)}
        idPrefix={`kitchen-${key}`}
      />
    ))}
    <ChoicePills
      label="Finish"
      options={swatches.finish}
      value={config.finish}
      onChange={(v) => onChange("finish", v)}
      idPrefix="kitchen-finish"
    />
  </>
);

const WARDROBE_OPTIONS = [
  { key: "laminate", label: "Laminate", source: "laminate" },
  { key: "glass", label: "Glass Finish", source: "glass" },
  { key: "wood", label: "Wood Texture", source: "wood" },
  { key: "handle", label: "Handle Finish", source: "handle" },
  { key: "wall", label: "Wall Color", source: "wall" },
];

export const WardrobeControls = ({ config, swatches, onChange }) => (
  <>
    <OptionRow
      label="Laminate"
      options={swatches.laminate}
      value={config.laminate}
      onChange={(v) => onChange("laminate", v)}
      idPrefix="wardrobe-laminate"
    />
    <ChoicePills
      label="Door Style"
      options={swatches.doorStyle}
      value={config.doorStyle}
      onChange={(v) => onChange("doorStyle", v)}
      idPrefix="wardrobe-doorstyle"
    />
    {WARDROBE_OPTIONS.filter((o) => o.key !== "laminate").map(({ key, label, source }) => (
      <OptionRow
        key={key}
        label={label}
        options={swatches[source]}
        value={config[key]}
        onChange={(v) => onChange(key, v)}
        idPrefix={`wardrobe-${key}`}
      />
    ))}
  </>
);
